class BlogGridFilter extends HTMLElement {
    constructor() {
        super();

        this.wrap = this.querySelector(".section-blog-grid-wrap");
        this.filterButtons = this.querySelectorAll(".blog-filter-btn");
        this.loadMoreBtn = this.querySelector("#LoadMoreBlogs");

        if (!this.wrap) return;

        this.blogUrl = this.dataset.blogUrl;
        this.sectionId = this.dataset.sectionId;
        this.desktopPerPage = Number(this.dataset.desktopPerPage) || 12;
        this.mobilePerPage = Number(this.dataset.mobilePerPage) || 3;

        this.currentTagUrl = this.blogUrl;
        this.isLoading = false;

        // How many of the currently-loaded cards should be visible right now.
        this.visibleCount = this.getPerPageForViewport();

        this.init();
    }

    init() {
        this.bindEvents();
        this.bindFilterScroll();
        this.applyVisibility();

        window.addEventListener("resize", () => {
            const newPerPage = this.getPerPageForViewport();
            if (newPerPage !== this.perPageCache) {
                this.perPageCache = newPerPage;
                // Only grow visibility on resize, never shrink content someone already loaded.
                this.visibleCount = Math.max(this.visibleCount, newPerPage);
                this.applyVisibility();
            }
        });
    }

    // Lets a normal mouse (wheel scrolls vertically, no shift key) move the
    // tag filter row horizontally, plus click-and-drag scrolling.
    bindFilterScroll() {
        const track = this.querySelector(".blog-filter");
        if (!track) return;

        track.addEventListener(
            "wheel",
            (e) => {
                if (track.scrollWidth <= track.clientWidth) return;
                // If the input is already mostly horizontal (trackpad/shift-wheel), leave it alone.
                if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
                e.preventDefault();
                track.scrollLeft += e.deltaY;
            },
            { passive: false }
        );

        let isDown = false;
        let dragged = false;
        let startX = 0;
        let startScrollLeft = 0;

        track.addEventListener("mousedown", (e) => {
            isDown = true;
            dragged = false;
            startX = e.pageX;
            startScrollLeft = track.scrollLeft;
            track.classList.add("dragging");
        });

        window.addEventListener("mousemove", (e) => {
            if (!isDown) return;
            const delta = e.pageX - startX;
            if (Math.abs(delta) > 5) dragged = true;
            if (dragged) {
                e.preventDefault();
                track.scrollLeft = startScrollLeft - delta;
            }
        });

        window.addEventListener("mouseup", () => {
            isDown = false;
            track.classList.remove("dragging");
        });

        // Swallow the click that follows a drag so a filter button doesn't
        // fire when the user was just dragging the row.
        track.addEventListener(
            "click",
            (e) => {
                if (dragged) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    dragged = false;
                }
            },
            true
        );
    }

    getPerPageForViewport() {
        this.perPageCache =
            window.innerWidth <= 767 ? this.mobilePerPage : this.desktopPerPage;
        return this.perPageCache;
    }

    bindEvents() {
        this.filterButtons.forEach((button) => {
            button.addEventListener("click", () => this.handleFilterClick(button));
        });

        if (this.loadMoreBtn) {
            this.loadMoreBtn.addEventListener("click", () => this.handleLoadMore());
        }
    }

    getCards() {
        return [...this.wrap.querySelectorAll(".blog-article-card")];
    }

    // --- Tag filter: always re-fetches page 1 from the server, using the
    //     button's real Shopify tag URL (e.g. /blogs/news/tagged/recipes) ---
    handleFilterClick(button) {
        if (this.isLoading) return;

        this.filterButtons.forEach((btn) => btn.classList.remove("active"));
        button.classList.add("active");

        this.currentTagUrl = button.dataset.url || this.blogUrl;
        this.visibleCount = this.getPerPageForViewport();

        this.fetchAndRender(this.decorateUrl(this.currentTagUrl), {
            replace: true,
        });
    }

    // --- Load more: reveal already-loaded cards first, only hit the server
    //     once we run out of locally-loaded cards ---
    handleLoadMore() {
        if (this.isLoading || !this.loadMoreBtn) return;

        const perPage = this.getPerPageForViewport();
        const loadedCount = this.getCards().length;
        const nextVisibleCount = this.visibleCount + perPage;

        if (nextVisibleCount <= loadedCount) {
            this.visibleCount = nextVisibleCount;
            this.applyVisibility();
            return;
        }

        const nextUrl = this.loadMoreBtn.dataset.nextUrl;
        if (!nextUrl) {
            this.visibleCount = loadedCount;
            this.applyVisibility();
            return;
        }

        this.visibleCount = nextVisibleCount;
        this.fetchAndRender(this.decorateUrl(nextUrl), { append: true });
    }

    // Ensures section_id is present so Shopify returns only this section's markup.
    decorateUrl(rawUrl) {
        const url = new URL(rawUrl, window.location.origin);
        url.searchParams.set("section_id", this.sectionId);
        return url.toString();
    }

    async fetchAndRender(url, { append = false, replace = false } = {}) {
        this.isLoading = true;
        this.classList.add("loading");
        if (this.loadMoreBtn) this.loadMoreBtn.disabled = true;

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Request failed: ${response.status}`);

            const html = await response.text();
            const doc = new DOMParser().parseFromString(html, "text/html");

            const newWrap = doc.getElementById(`BlogGridWrap-${this.sectionId}`);
            const newCards = newWrap
                ? [...newWrap.querySelectorAll(".blog-article-card")]
                : [];
            const newLoadMoreBtn = doc.getElementById("LoadMoreBlogs");

            if (replace) {
                this.wrap.innerHTML = "";
            }

            newCards.forEach((card) => this.wrap.appendChild(card));

            if (this.loadMoreBtn) {
                if (newLoadMoreBtn && newLoadMoreBtn.dataset.nextUrl) {
                    this.loadMoreBtn.dataset.nextUrl = newLoadMoreBtn.dataset.nextUrl;
                } else {
                    delete this.loadMoreBtn.dataset.nextUrl;
                }
            }

            if (replace && newCards.length === 0) {
                // No results for this tag; nothing to reveal.
                this.visibleCount = 0;
            }

            this.applyVisibility();

            if (replace) {
                history.replaceState({}, "", this.currentTagUrl);
            }
        } catch (err) {
            console.error("Blog grid fetch failed:", err);
        } finally {
            this.isLoading = false;
            this.classList.remove("loading");
            if (this.loadMoreBtn) this.loadMoreBtn.disabled = false;
        }
    }

    applyVisibility() {
        const cards = this.getCards();

        cards.forEach((card, index) => {
            card.classList.toggle("hidden", index >= this.visibleCount);
        });

        if (!this.loadMoreBtn) return;

        const hasMoreLocally = cards.length > this.visibleCount;
        const hasMoreOnServer = Boolean(this.loadMoreBtn.dataset.nextUrl);

        this.loadMoreBtn.classList.toggle(
            "hidden",
            !(hasMoreLocally || hasMoreOnServer)
        );
    }
}

customElements.define("blog-grid-filter", BlogGridFilter);